import { linkUnassociatedMailSchema, type UnassociatedMailItem } from '@easyhelp/shared';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import { useId, useState, type FormEvent } from 'react';

export interface LinkMailDialogProps {
  mail: UnassociatedMailItem;
  /** Errore dell'API, mostrato nel dialogo senza chiuderlo (es. ticket inesistente). */
  error: string | null;
  busy: boolean;
  onSubmit: (ticketCode: string) => void;
  onClose: () => void;
}

/**
 * Collegamento di un'email della posta non associata a un ticket esistente (#42, PAGE-008): il
 * codice si scrive com'è (senza distinzione di maiuscole) e lo verifica l'API. Smontato alla
 * chiusura, così il campo riparte vuoto.
 */
export function LinkMailDialog({ mail, error, busy, onSubmit, onClose }: LinkMailDialogProps) {
  const titleId = useId();
  const [code, setCode] = useState('');
  const [invalid, setInvalid] = useState<string | null>(null);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const parsed = linkUnassociatedMailSchema.safeParse({ ticketCode: code });
    if (!parsed.success) {
      setInvalid(parsed.error.issues[0]?.message ?? 'Codice del ticket non valido');
      return;
    }
    setInvalid(null);
    onSubmit(parsed.data.ticketCode);
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} aria-labelledby={titleId} fullWidth maxWidth="sm">
      <form onSubmit={submit} noValidate>
        <DialogTitle id={titleId}>Collega a un ticket</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            L’email di {mail.sender} entra nel thread del ticket come messaggio dell’utente, all’istante in cui è
            arrivata. Se il mittente non è chi ha segnalato, il messaggio è marcato «non riconosciuto». Lo stato del
            ticket non cambia; se non ne sei il responsabile, il responsabile riceve un avviso (senza il testo).
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            label="Codice del ticket"
            placeholder="EC-2026-0001"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            error={invalid !== null}
            helperText={invalid ?? ' '}
            disabled={busy}
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>
            Annulla
          </Button>
          <Button type="submit" variant="contained" disabled={busy}>
            {busy ? 'Collegamento…' : 'Collega'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
